import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { ApiError } from "../api/client.js";
import { fetchEvent, queryKeys } from "../api/queries.js";
import { JsonTree } from "../components/JsonTree.js";

// Extracts an event's `symbol` field if present (most position/safety
// kinds carry one) so the detail view can deep-link to the per-symbol
// position timeline (Faz 8.2 cross-link).
const extractSymbol = (payload: unknown): string | null => {
  if (payload === null || typeof payload !== "object") return null;
  const v = (payload as { symbol?: unknown }).symbol;
  return typeof v === "string" && v.length > 0 ? v : null;
};

export const EventDetailView = (): ReactElement => {
  const params = useParams<{ tickId: string }>();
  const tickId = params.tickId ?? "";

  const query = useQuery({
    queryKey: queryKeys.event(tickId),
    queryFn: () => fetchEvent(tickId),
    enabled: tickId !== "",
  });

  return (
    <section className="space-y-4">
      <header className="flex items-center gap-3">
        <Link to="/events" className="text-sm text-sky-400 hover:underline">
          ← Back to timeline
        </Link>
        <h2 className="text-lg font-semibold">Event {tickId}</h2>
      </header>

      {query.isLoading && <p data-testid="loading">Loading…</p>}
      {query.isError && (
        <p className="text-rose-400" data-testid="error">
          {query.error instanceof ApiError
            ? `${query.error.server.kind}: ${query.error.server.message}`
            : "Unknown error"}
        </p>
      )}

      {query.data && (
        <>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="font-semibold text-slate-400">kind</dt>
            <dd className="font-mono">{query.data.event.kind}</dd>
            <dt className="font-semibold text-slate-400">tickId</dt>
            <dd className="font-mono">{String(query.data.event.tickId)}</dd>
            <dt className="font-semibold text-slate-400">ts</dt>
            <dd className="font-mono">{String(query.data.event.ts)}</dd>
            <dt className="font-semibold text-slate-400">correlationId</dt>
            <dd className="font-mono">
              <Link
                to={`/lineage/${encodeURIComponent(query.data.event.correlationId)}`}
                className="text-emerald-400 hover:underline"
              >
                {query.data.event.correlationId}
              </Link>
            </dd>
            <dt className="font-semibold text-slate-400">configHash</dt>
            <dd className="font-mono text-xs text-slate-400">{query.data.configHash}</dd>
            <dt className="font-semibold text-slate-400">version</dt>
            <dd className="font-mono">{query.data.event.version}</dd>
            {extractSymbol(query.data.event.payload) !== null && (
              <>
                <dt className="font-semibold text-slate-400">symbol</dt>
                <dd className="font-mono">
                  <Link
                    to={`/positions/${encodeURIComponent(extractSymbol(query.data.event.payload)!)}`}
                    className="text-violet-300 hover:underline"
                    data-testid="position-timeline-link"
                  >
                    {extractSymbol(query.data.event.payload)}
                  </Link>
                </dd>
              </>
            )}
          </dl>

          <div>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-400">
              payload
            </h3>
            <JsonTree value={query.data.event.payload} />
          </div>
        </>
      )}
    </section>
  );
};
